/**
 * Visual identity for a person when there is NO photo: initials + a stable
 * estable.
 *
 * This is the ONLY implementation of these three functions in the monorepo.
 * Previously each screen resolved initials on its own with `charAt(0)` and
 * different rules (some fell back to email, others did not; some uppercased, others
 * did not), so the same person looked different depending on where you viewed them.
 *
 * Pure and dependency-free on purpose: the web, desktop, mobile, and tests all
 * use it, and it must give EXACTLY the same result everywhere.
 */

/** How many initials the fallback avatar carries. */
const MAX_INITIALS = 2;

/** How many color pairs the palette has (see `avatar.tsx`). */
export const AVATAR_PALETTE_SIZE = 8;

/**
 * Initials for the fallback avatar.
 *
 * Takes up to two, from the first and last words of the name. If there is no
 * name it falls back to email, and if there is no email either, to a middle dot
 * (`·`) — never a question mark, which reads as an error.
 *
 * Uses `codePointAt` and not `[0]`: a name beginning with an emoji or a
 * character outside the basic plane would split into two illegible halves with
 * direct indexing, because JavaScript indexes by UTF-16 code unit.
 */
export function initialsOf(name?: string | null, email?: string | null): string {
  const source = (name ?? "").trim() || (email ?? "").trim().split("@")[0] || "";
  const parts = source.split(/[\s._-]+/u).filter(Boolean);
  if (parts.length === 0) {
    return "·";
  }
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts.at(-1) ?? "") : "";
  return [first, last]
    .filter(Boolean)
    .slice(0, MAX_INITIALS)
    .map((part) => String.fromCodePoint(part.codePointAt(0) ?? 0).toLocaleUpperCase())
    .join("");
}

/**
 * Stable color index for a person, in `[0, AVATAR_PALETTE_SIZE)`.
 *
 * Derives from the **userId and not the name**: the name changes (a typo is
 * corrected, a surname is added) and the color must not change with it, or the
 * person "becomes someone else" to anyone who recognized them by color.
 *
 * 32-bit FNV-1a: cheap, dependency-free, and well distributed for short
 * strings. It is not cryptographic and does not aim to be.
 */
export function avatarPaletteIndex(userId?: string | null): number {
  // WARNING: do not turn this into a default parameter
  // (`userId: string | null = ""`). The pre-commit `oxlint --fix` tries this
  // via `unicorn/prefer-default-parameters`, and it would be a SEMANTIC CHANGE:
  // default values apply only to `undefined`, so a `null`—exactly what
  // arrives from a row without a user—would pass through and crash on
  // `.length`.
  const key = typeof userId === "string" ? userId : "";

  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < key.length; i++) {
    // `charCodeAt` and NOT `codePointAt`: we want to traverse UTF-16 code units
    // one at a time. `codePointAt` returns the FULL code point for surrogate
    // pairs (and `undefined` past the end), so it would change the hash and,
    // with it, the color of people who already had one assigned. The pre-commit
    // `oxlint --fix` also tries this substitution
    // (`unicorn/prefer-code-point`); it is wrong here.
    // oxlint-disable-next-line unicorn/prefer-code-point
    hash ^= key.charCodeAt(i);
    // `Math.imul` keeps multiplication at 32 bits; with `*`,
    // precision is lost past 2^53 and the hash is no longer
    // the same on every platform.
    hash = Math.imul(hash, 0x01_00_01_93);
  }
  return Math.abs(hash) % AVATAR_PALETTE_SIZE;
}

/**
 * Absolute avatar URL from the KEY stored in `user.image`.
 *
 * The row stores the key (`avatars/{userId}/{hash}.webp`), not an
 * absolute URL, so the row remains portable across dev/rc/prod —which have
 * different API origins— and a domain change does not require
 * rewriting the database.
 *
 * Tolerates the stored value already being an absolute URL: a row written by
 * an earlier version does not appear broken.
 */
export function avatarUrl(key?: string | null, apiBaseUrl?: string | null): string | null {
  const value = (key ?? "").trim();
  if (!value) {
    return null;
  }
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  const base = (apiBaseUrl ?? "").replace(/\/+$/u, "");
  return `${base}/api/avatar/${value}`;
}
